import React from "react";
import { Field } from "../../../components/Field";
import { SelectField } from "../../../components/SelectField";
import DatePicker from "../../components/DatePicker";
import Loading from "../../components/Loading";
import UserPermissionTab from "./components/UserPermissionTab";
import Modal from "../Modal";
import CustomSelect from "../CustomSelect";
import { GENDER_OPTIONS, STATUS_OPTIONS } from "./constants";
import { formatDate } from "../../../../utils/DateFormat";
import { reverseDateFormat } from "../../../../utils/DateFormat";

const EmployeeDetailForm = ({
  mode,
  data,
  activeTab,
  setActiveTab,
  uploading,
  loading,
  loadingMeta,
  isFetchingEmployeeDetail,
  isAddingExisting,
  setIsAddingExisting,
  selectedExistingEmployee,
  imageSrc,
  searchQuery,
  searchLoading,
  hasLoadedEmployees,
  hasMoreEmployees,
  loadingMoreEmployees,

  roleOptions,
  departmentOptions,
  employeeOptions,
  loadingRoles,

  onCancel,
  onBack,
  onEditClick,
  onChange,
  onImageChange,
  onSelectExistingEmployee,
  onSearchQueryChange,
  onDropdownOpenEmployees,
  onDropdownOpenRoles,
  onLoadMoreEmployees,
  onSubmit,
  onSavePermission,

  fileInputRef,
  permissionTabRef,

  title,
  layout,
  isOpen,
  employeeId,
  departmentContextId,
  canEdit,
}) => {
  if (loading || loadingMeta) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!data) return null;

  const selectedRole = roleOptions.find((o) => o.value === data.roleId) ||
    (data.roleId ? { label: data.roleLabel } : null);

  const roleLabel = String(selectedRole?.label || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .trim()
    .toLowerCase();
  const shouldHideResponsibleDepartments = [
    "nhan vien (cms)",
    "nhan vien ( cms )",
    "staff (cms)",
    "staff ( cms )",
    "quan ly phong ban",
    "ql phong ban",
  ].includes(roleLabel);

  const content = (
    <div className={`flex flex-col h-full ${layout === "page" ? "bg-white" : ""}`}>
      {(mode === "view" || mode === "edit") && (
        <div className="flex px-6 border-b border-gray-100">
          <button
            onClick={() => setActiveTab("info")}
            className={`py-4 px-6 font-medium text-sm transition-all border-b-2 ${activeTab === "info"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Thông tin nhân viên
          </button>
          <button
            onClick={() => setActiveTab("permission")}
            className={`py-4 px-6 font-medium text-sm transition-all border-b-2 ${activeTab === "permission"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Phân quyền
          </button>
        </div>
      )}

      <div className={`flex-1 ${layout === "popup" ? "overflow-y-auto" : ""} p-6 min-h-[650px]`}>
        {activeTab === "info" ? (
          <>
            {mode === "create" && departmentContextId && (
              <div className="mb-6">
                <div className="flex gap-3 p-1 bg-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setIsAddingExisting(false)}
                    className={`flex-1 py-3 px-4 rounded-md font-medium text-sm transition-all duration-200 ${!isAddingExisting
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                      }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Tạo nhân viên mới</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsAddingExisting(true)}
                    className={`flex-1 py-3 px-4 rounded-md font-medium text-sm transition-all duration-200 ${isAddingExisting
                      ? "bg-white text-green-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                      }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>Thêm nhân viên có sẵn</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {mode === "create" && isAddingExisting && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                {!hasLoadedEmployees && searchLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loading />
                    <span className="ml-2 text-sm text-gray-600">Đang tải danh sách nhân viên...</span>
                  </div>
                ) : employeeOptions.length > 1 || searchQuery ? (
                  <>
                    <div className="flex items-start gap-3 mb-3">
                      <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-900 mb-1">Chọn nhân viên từ hệ thống</p>
                        <p className="text-sm text-green-700">Tìm và chọn nhân viên để thêm vào phòng ban này</p>
                      </div>
                    </div>

                    <div>
                      <CustomSelect
                        options={employeeOptions}
                        value={employeeOptions.find((opt) => opt.value === selectedExistingEmployee) || employeeOptions[0]}
                        onChange={(opt) => onSelectExistingEmployee(opt?.value || "")}
                        onDropdownOpen={onDropdownOpenEmployees}
                        searchable={true}
                        onSearch={onSearchQueryChange}
                        placeholder="Tìm kiếm nhân viên..."
                        height="44px"
                        infinite={true}
                        hasMore={hasMoreEmployees}
                        loadingMore={loadingMoreEmployees}
                        loadMore={onLoadMoreEmployees}
                      />
                    </div>

                    {selectedExistingEmployee && (
                      <div className="mt-3 p-3 bg-white border border-green-300 rounded-lg">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-green-800 font-medium">Nhân viên đã chọn - Chỉ cập nhật phòng ban</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900 mb-1">Không có nhân viên khả dụng</p>
                      <p className="text-sm text-amber-700">Tất cả nhân viên đã được phân công vào phòng ban. Vui lòng chọn "Tạo nhân viên mới".</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col items-center mb-6 relative">
              {isFetchingEmployeeDetail && (
                <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center rounded-lg">
                  <Loading size="small" />
                </div>
              )}
              <div className="relative w-32 h-32">
                <div className="w-full h-full rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  <img src={imageSrc} alt="Avatar" className="w-full h-full object-cover" />
                </div>

                {canEdit && !isAddingExisting && (
                  <>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-full bg-black/30 cursor-pointer">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={onImageChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </>
                )}
              </div>
            </div>

            <div className={`grid grid-cols-2 gap-5 transition-opacity duration-200 ${isFetchingEmployeeDetail ? "opacity-50 pointer-events-none" : ""}`}>
              {mode !== "create" && <Field label="Mã NV" disabled value={data.staffCode} />}

              <Field
                label="Họ tên"
                required
                value={data.fullName}
                disabled={!canEdit || isAddingExisting}
                placeholder="Nhập họ tên"
                onChange={(v) => onChange("fullName", v)}
              />

              <Field
                label="Email"
                required
                value={data.email}
                disabled={!canEdit || isAddingExisting}
                placeholder="Nhập email"
                onChange={(v) => onChange("email", v)}
              />

              <Field
                label="Số điện thoại"
                value={data.phone}
                disabled={!canEdit || isAddingExisting}
                placeholder="Nhập số điện thoại"
                onChange={(v) => onChange("phone", v)}
              />

              <SelectField
                label="Chức vụ"
                required
                disabled={!canEdit || isAddingExisting}
                options={loadingRoles ? [{ value: "", label: "Đang tải..." }] : roleOptions}
                value={
                  roleOptions.find((o) => o.value === data.roleId) ||
                  (data.roleId ? { value: data.roleId, label: data.roleLabel || data.roleId } : undefined)
                }
                onChange={(opt) => onChange("roleId", opt?.value)}
                onDropdownOpen={onDropdownOpenRoles}
              />

              <SelectField
                label={"Ph\u00f2ng ban tr\u1ef1c thu\u1ed9c"}
                disabled={!canEdit}
                options={departmentOptions}
                value={departmentOptions.find((o) => o.value === data.departmentId)}
                onChange={(opt) => onChange("departmentId", opt?.value)}
              />

              {!shouldHideResponsibleDepartments && (
                <div className="col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">{"Ph\u00f2ng ban ph\u1ee5 tr\u00e1ch"}</label>
                  {canEdit ? (
                    <CustomSelect
                      isMulti
                      options={departmentOptions}
                      value={departmentOptions.filter((o) => (data.responsibleDepartments || []).includes(o.value))}
                      onChange={(selected) => onChange("responsibleDepartments", (selected || []).map((s) => s.value))}
                      placeholder={"Ch\u1ecdn c\u00e1c ph\u00f2ng ban ph\u1ee5 tr\u00e1ch..."}
                      height="auto"
                      controlClassName="min-h-[52px] rounded-lg"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(data.responsibleDepartments || []).length > 0 ? (
                        (data.responsibleDepartments || []).map((id) => {
                          const dept = departmentOptions.find((o) => o.value === id);
                          return (
                            <span key={id} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium border border-blue-100">
                              {dept?.label || id}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-sm text-gray-400">{"Ch\u01b0a c\u00f3"}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <SelectField
                label="Giới tính"
                disabled={!canEdit || isAddingExisting}
                options={GENDER_OPTIONS}
                value={GENDER_OPTIONS.find((o) => o.value === data.gender)}
                onChange={(opt) => onChange("gender", opt?.value)}
              />

              <div>
                <label className="block text-sm text-gray-600 mb-1">Ngày sinh</label>
                {canEdit && !isAddingExisting ? (
                  <DatePicker
                    value={data.dateOfBirthStr}
                    onChange={(str) => {
                      onChange("dateOfBirthStr", str);
                      onChange("dateOfBirth", str ? new Date(reverseDateFormat(str)) : null);
                    }}
                    width="100%"
                    placeholder="Chọn ngày"
                    height="52px"
                  />
                ) : (
                  <div className="text-sm font-medium mt-2">{formatDate(data.dateOfBirth) || "--"}</div>
                )}
              </div>

              <SelectField
                label="Trạng thái"
                disabled={!canEdit || isAddingExisting}
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((o) => o.value === data.status)}
                onChange={(opt) => onChange("status", opt?.value)}
              />

              {canEdit && !isAddingExisting && (
                <Field
                  label={mode === "create" ? "Mật khẩu" : "Mật khẩu mới"}
                  value={data.password}
                  type="password"
                  onChange={(v) => onChange("password", v)}
                  autoComplete="new-password"
                />
              )}
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 mb-2">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-sm bg-gray-200">
                <img src={imageSrc} alt="Avatar" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{data?.fullName || "N/A"}</h3>
                {data?.staffCode && <p className="text-xs text-gray-500 font-medium">Mã NV: {data.staffCode}</p>}
                <p className="text-sm text-blue-600 font-medium">{data?.roleLabel || "Chưa xác định"}</p>
              </div>
            </div>

            <UserPermissionTab ref={permissionTabRef} isReadOnly={mode === "view"} staffId={employeeId} />
          </div>
        )}
      </div>

      {layout === "page" && (
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white">
          {mode === "view" ? (
            <button
              type="button"
              onClick={onEditClick}
              className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium flex items-center gap-2 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Chỉnh sửa thông tin
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-all duration-200"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={() => {
                  if (activeTab === "permission") {
                    onSavePermission();
                  } else {
                    onSubmit();
                  }
                }}
                disabled={uploading}
                className={`px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium transition-all duration-200 shadow-sm ${uploading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
              >
                {uploading ? (
                  <div className="flex items-center gap-2">
                    <Loading size="small" />
                    <span>Đang xử lý...</span>
                  </div>
                ) : activeTab === "permission" ? (
                  "Lưu thông tin"
                ) : mode === "create" ? (
                  "Tạo nhân viên"
                ) : (
                  "Lưu thay đổi"
                )}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (layout === "page") {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 mx-auto overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors group" title="Quay lại">
              <svg className="w-5 h-5 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          )}
          <h1 className="text-xl font-bold">
            {title || (mode === "create" ? "Thêm nhân viên mới" : mode === "edit" ? "Chỉnh sửa thông tin" : "Chi tiết nhân viên")}
          </h1>
        </div>
        {content}
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title || (mode === "create" ? "Thêm nhân viên mới" : mode === "edit" ? "Chỉnh sửa thông tin" : "Chi tiết nhân viên")}
      maxWidth="1200px"
      showCancelButton={mode === "view" || canEdit}
      cancelButtonText="Hủy"
      showSecondaryButton={false}
      primaryButtonText={
        mode === "view"
          ? null
          : activeTab === "permission"
            ? "Lưu thông tin"
            : uploading
              ? mode === "create"
                ? "Đang tạo..."
                : "Đang lưu..."
              : mode === "create"
                ? "Thêm nhân viên"
                : "Lưu thay đổi"
      }
      primaryButtonOnClick={() => {
        if (mode === "view") {
          onEditClick?.();
        } else if (activeTab === "permission") {
          onSavePermission();
        } else {
          onSubmit();
        }
      }}
      primaryButtonVariant={mode === "view" ? "outline" : "primary"}
    >
      {content}
    </Modal>
  );
};

export default EmployeeDetailForm;
